/**
 * Customer management API schemas
 */

import { z } from 'zod';
import { OpaqueCursorPaginationSchema } from '../authorization.types';
import type {
    CreateMerchantCustomerRecordRequest,
    CustomerUserSummary,
    CustomerUsersListItem,
    CustomerUsersPageResponse,
    CustomerUsersSearchRequest,
    MerchantCustomerRecordDetailsResponse,
    MerchantCustomerRecordIpwLookupRequest,
    MerchantCustomerRecordIpwLookupResponse,
    MerchantCustomerRecordListItem,
    MerchantCustomerRecordSearchListItem,
    MerchantCustomerRecordSearchPageResponse,
    MerchantCustomerRecordSearchRequest,
    UpdateMerchantCustomerRecordRequest,
    UpsertIpwMerchantCustomerRecordRequest,
    UpsertIpwMerchantCustomerRecordResponse,
} from '../customer.types';
import type { Status } from '../identity-contracts';
import { AuthorizationOpaqueCursorQuerySchema } from './authorization.schemas';
import { SearchTermsSchema } from './omnisearch.schemas';
import { CustomerDetailsSchema } from './customer-details.schemas';
import { NumericIpwSystemIdSchema } from './third-party-system.schemas';
export * from '../customer.types';

export const MERCHANT_CUSTOMER_RECORDS_MAX_PAGE_LIMIT: number = 1000;

export const MerchantCustomerRecordStatusSchema: z.ZodType<Status> = z.enum([
    'PENDING',
    'ACTIVE',
    'DISABLED',
    'DELETED',
]);

export const CustomerUsersPaginationLimitQuerySchema: z.ZodType<number | undefined> = z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .transform((value: number | undefined) => {
        if (value === undefined) {
            return undefined;
        }
        return Math.min(value, MERCHANT_CUSTOMER_RECORDS_MAX_PAGE_LIMIT);
    });

export const CustomerUsersPaginationCursorQuerySchema: z.ZodType<string | undefined> =
    AuthorizationOpaqueCursorQuerySchema;

export const CustomerUsersSearchRequestSchema: z.ZodType<CustomerUsersSearchRequest> = z
    .object({
        terms: SearchTermsSchema,
        limit: CustomerUsersPaginationLimitQuerySchema,
        cursor: CustomerUsersPaginationCursorQuerySchema,
    })
    .strict();

export const CustomerUserSummarySchema: z.ZodType<CustomerUserSummary> = z.object({
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    primary_email: z.string().email(),
    status: MerchantCustomerRecordStatusSchema,
});

export const CustomerUsersListItemSchema: z.ZodType<CustomerUsersListItem> = z.object({
    customer_organization_uuid: z.string().uuid(),
    customer_organization_name: z.string(),
    customer_organization_status: MerchantCustomerRecordStatusSchema,
    customer_user_count: z.number().int().nonnegative(),
    customer_user: CustomerUserSummarySchema.nullable(),
});

export const CustomerUsersPageResponseSchema: z.ZodType<CustomerUsersPageResponse> = z.object({
    customer_users: z.array(CustomerUsersListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

/**
 * Merchant-scoped customer-record list item
 */
export const MerchantCustomerRecordListItemSchema: z.ZodType<
    MerchantCustomerRecordListItem
> = z.object({
    merchant_customer_record_uuid: z.string().uuid(),
    name: z.string(),
    is_organizational: z.boolean(),
    merchant_organization_uuid: z.string().uuid(),
    merchant_name: z.string().optional(),
    customer_organization_uuid: z.string().uuid().optional(),
    primary_email: z.string().email().optional(), // For individual customers
});

export const MerchantCustomerRecordSearchPaginationLimitQuerySchema: z.ZodType<
    number | undefined
> = z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .transform((value: number | undefined) => {
        if (value === undefined) {
            return undefined;
        }
        return Math.min(value, MERCHANT_CUSTOMER_RECORDS_MAX_PAGE_LIMIT);
    });

export const MerchantCustomerRecordSearchPaginationCursorQuerySchema: z.ZodType<
    string | undefined
> = AuthorizationOpaqueCursorQuerySchema;

export const MerchantCustomerRecordSearchRequestSchema: z.ZodType<
    MerchantCustomerRecordSearchRequest
> = z
    .object({
        terms: SearchTermsSchema,
        limit: MerchantCustomerRecordSearchPaginationLimitQuerySchema,
        cursor: MerchantCustomerRecordSearchPaginationCursorQuerySchema,
        merchant_organization_uuid: z.string().uuid().optional(),
        show_deleted: z.boolean().optional(),
    })
    .strict();

export const MerchantCustomerRecordSearchListItemSchema: z.ZodType<
    MerchantCustomerRecordSearchListItem
> = z.object({
    merchant_customer_record_uuid: z.string().uuid(),
    name: z.string(),
    is_organizational: z.boolean(),
    primary_email: z.string().email().optional(),
    primary_phone: z.string().optional(),
    status: MerchantCustomerRecordStatusSchema,
    merchant_organization_uuid: z.string().uuid(),
    merchant_name: z.string().optional(),
    customer_organization_uuid: z.string().uuid().optional(),
    customer_organization_name: z.string().optional(),
    last_payment_request_uuid: z.string().uuid().optional(),
    last_payment_request_reference_number: z.string().optional(),
    last_payment_activity_at: z.string().datetime().optional(),
});

export const MerchantCustomerRecordSearchPageResponseSchema: z.ZodType<
    MerchantCustomerRecordSearchPageResponse
> = z.object({
    merchant_customer_records: z.array(MerchantCustomerRecordSearchListItemSchema),
    pagination: OpaqueCursorPaginationSchema,
});

export const MerchantCustomerRecordDetailsResponseSchema: z.ZodType<
    MerchantCustomerRecordDetailsResponse
> = z.object({
    merchant_customer_record_uuid: z.string().uuid(),
    name: z.string(),
    is_organizational: z.boolean(),
    status: MerchantCustomerRecordStatusSchema,
    merchant_organization_uuid: z.string().uuid(),
    merchant_name: z.string().optional(),
    customer_organization_uuid: z.string().uuid().optional(),
    customer_organization_name: z.string().optional(),
    primary_email: z.string().email().optional(),
    primary_phone: z.string().optional(),
    customer_details: CustomerDetailsSchema,
});

export const MerchantCustomerRecordIpwLookupRequestSchema: z.ZodType<
    MerchantCustomerRecordIpwLookupRequest
> = z
    .object({
        ipw_system_id: NumericIpwSystemIdSchema,
        customer_name: z.string().min(1),
        main_phone: z.string().min(1).optional(),
        merchant_organization_uuid: z.string().uuid().optional(),
    })
    .strict()
    .meta({
        id: 'MerchantCustomerRecordIpwLookupRequest',
        title: 'IPW customer lookup request',
        description:
            'IPW customer identity and optional merchant scope used to find billing details.',
    });

export const MerchantCustomerRecordIpwLookupResponseSchema: z.ZodType<
    MerchantCustomerRecordIpwLookupResponse
> = z.object({
    merchant_customer_records: z.array(MerchantCustomerRecordDetailsResponseSchema),
}).meta({
    id: 'MerchantCustomerRecordIpwLookupResponse',
    title: 'IPW customer lookup response',
    description: 'Normalized current customer records matching the IPW customer identity.',
});

export const UpsertIpwMerchantCustomerRecordRequestSchema: z.ZodType<
    UpsertIpwMerchantCustomerRecordRequest
> = z
    .object({
        ipw_system_id: NumericIpwSystemIdSchema,
        ipw_customer_id: z.string().trim().min(1),
        customer_details: CustomerDetailsSchema,
    })
    .strict();

export const UpsertIpwMerchantCustomerRecordResponseSchema: z.ZodType<
    UpsertIpwMerchantCustomerRecordResponse
> = MerchantCustomerRecordDetailsResponseSchema;

/**
 * Create merchant-scoped customer record request
 */
export const CreateMerchantCustomerRecordRequestSchema: z.ZodType<
    CreateMerchantCustomerRecordRequest
> = z.object({
    name: z.string().min(1),
    merchant_organization_uuid: z.string().uuid(),
    is_organizational: z.boolean(),
    primary_email: z.string().email().optional(), // For individual customers
});

/**
 * Update merchant-scoped customer record request
 */
export const UpdateMerchantCustomerRecordRequestSchema: z.ZodType<
    UpdateMerchantCustomerRecordRequest
> = z.object({
    name: z.string().min(1).optional(),
    status: MerchantCustomerRecordStatusSchema.optional(),
    primary_email: z.string().email().optional(),
});
